import { useEffect, useRef } from "react";

/**
 * Minimum gap between attention-driven labor touches. Keeps dev countdown storage and React
 * work bounded while still resetting idle well before the default 15-minute window.
 */
export const LABOR_ATTENTION_TOUCH_MIN_INTERVAL_MS = 2_000;

export const LABOR_ATTENTION_CUSTOM_EVENT = "sgs:labor-attention";

/**
 * Signals human or workflow activity (such as a camera barcode scan or floor mutation)
 * when a synthetic callback bypasses normal DOM pointer/key events.
 */
export function notifyLaborAttentionActivity(): void {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return;
  }
  if (typeof CustomEvent === "function") {
    window.dispatchEvent(new CustomEvent(LABOR_ATTENTION_CUSTOM_EVENT));
  }
}

/**
 * Pointer, keys, scroll, wheel, and focus movement anywhere in the window count as labor
 * activity. Events are throttled so we do not spam `touchActivity`. A successful heartbeat
 * flush (on its interval, only when activity advanced) is what advances `last_heartbeat_at`.
 */
export function useLaborAttentionActivity(onAttention: () => void, enabled: boolean): void {
  const onRef = useRef(onAttention);
  onRef.current = onAttention;

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    let lastFire = 0;

    const maybeFire = (): void => {
      const now = Date.now();
      if (now - lastFire < LABOR_ATTENTION_TOUCH_MIN_INTERVAL_MS) {
        return;
      }
      lastFire = now;
      onRef.current();
    };

    const passiveCapture = { capture: true, passive: true } as const;
    const scrollCapture = { capture: true, passive: true } as const;
    const keyCapture = { capture: true, passive: true } as const;

    window.addEventListener("pointerdown", maybeFire, passiveCapture);
    window.addEventListener("pointermove", maybeFire, passiveCapture);
    window.addEventListener("touchstart", maybeFire, passiveCapture);
    window.addEventListener("touchmove", maybeFire, passiveCapture);
    window.addEventListener("wheel", maybeFire, passiveCapture);
    window.addEventListener("scroll", maybeFire, scrollCapture);
    window.addEventListener("keydown", maybeFire, keyCapture);
    window.addEventListener("focusin", maybeFire, passiveCapture);
    window.addEventListener(LABOR_ATTENTION_CUSTOM_EVENT, maybeFire);

    return () => {
      window.removeEventListener("pointerdown", maybeFire, passiveCapture);
      window.removeEventListener("pointermove", maybeFire, passiveCapture);
      window.removeEventListener("touchstart", maybeFire, passiveCapture);
      window.removeEventListener("touchmove", maybeFire, passiveCapture);
      window.removeEventListener("wheel", maybeFire, passiveCapture);
      window.removeEventListener("scroll", maybeFire, scrollCapture);
      window.removeEventListener("keydown", maybeFire, keyCapture);
      window.removeEventListener("focusin", maybeFire, passiveCapture);
      window.removeEventListener(LABOR_ATTENTION_CUSTOM_EVENT, maybeFire);
    };
  }, [enabled]);
}
